import { useEffect, useState } from "react";
import { Check, Download } from "lucide-react";
import { securitySummary, securitySummaryPdfUrl } from "../../lib/api";
import { useLang, setLang } from "../../lib/i18n";

const T = {
  en: { eyebrow: "Security at a glance", title: "The short version, in eight lines.", pdf: "One-page PDF", foot: "Share the PDF with a family member, advisor or attorney who asks how the vault is protected.", updated: "Updated" },
  es: { eyebrow: "La seguridad, en resumen", title: "La versión corta, en ocho líneas.", pdf: "PDF de una página", foot: "Comparta el PDF con un familiar, asesor o abogado que pregunte cómo se protege la bóveda.", updated: "Actualizado en" },
};

export const SecurityAtAGlance = () => {
  const { lang: appLang } = useLang();
  const lang = appLang === "es" ? "es" : "en";
  const t = T[lang];
  const [d, setD] = useState(null);
  useEffect(() => { securitySummary(lang).then(setD).catch(() => {}); }, [lang]);
  useEffect(() => {
    if (!d || window.location.hash !== "#at-a-glance") return;
    const id = setTimeout(() => document.getElementById("at-a-glance")?.scrollIntoView({ block: "start" }), 150);
    return () => clearTimeout(id);
  }, [d]);
  if (!d) return null;
  return (
    <section id="at-a-glance" className="mt-10 rounded-3xl border border-border bg-card p-7 sm:p-9 scroll-mt-24" data-testid="trust-at-a-glance" lang={lang}>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-primary">{t.eyebrow}</div>
          <h2 className="font-serif text-2xl sm:text-3xl mt-1">{t.title}</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex rounded-full border border-border bg-background p-0.5 text-xs" role="group" data-testid="trust-summary-lang">
            {[["en", "English"], ["es", "Español"]].map(([k, l]) => <button key={k} type="button" onClick={() => setLang(k, { sync: false })} aria-pressed={lang === k} data-testid={`trust-summary-lang-${k}`} className={`rounded-full px-3 py-1 ${lang === k ? "bg-foreground text-background" : "text-muted-foreground"}`}>{l}</button>)}
          </div>
          <a href={`${securitySummaryPdfUrl}?lang=${lang}`} target="_blank" rel="noopener noreferrer" data-testid="trust-summary-pdf-link" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-4 py-2 text-sm font-medium hover:bg-primary/90 transition-colors"><Download className="h-4 w-4" /> {t.pdf}</a>
        </div>
      </div>
      <ul className="mt-6 grid md:grid-cols-2 gap-x-8 gap-y-4">
        {d.points.map((p, i) => (
          <li key={p.title} className="flex gap-3" data-testid={`trust-glance-${i}`}>
            <Check className="h-4 w-4 text-primary mt-1 shrink-0" />
            <div><h3 className="font-medium text-sm">{p.title}</h3><p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{p.body}</p></div>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-[11px] text-muted-foreground">{t.updated} {d.updated}. {t.foot}</p>
    </section>
  );
};
